import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { mediaUrl, formatBlogDate } from '../utils/blogUtils';
import { parseTuskersStatusBody } from '../utils/parseTuskersStatusBody';
import { soccerPath } from '../utils/paths';
import BlogShareBar from './BlogShareBar';
import { useBlogPostMeta } from '../hooks/useBlogPostMeta';
import './TuskersLiveStatusBlogArticle.css';

const LIVE_STATUS_SLUG = 'columbus-tuskers-live-status';

function resolveHref(url) {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/soccer/')) return url;
  if (url.startsWith('/')) return soccerPath(url);
  return url;
}

function linkLabel(url, fallback) {
  if (!url) return fallback;
  if (url.includes('khiladiz.com/maveli-cup/standings')) return 'Khiladiz standings';
  if (url.includes('khiladiz.com/maveli-cup/matches')) return 'Khiladiz schedule';
  if (url.includes('comaohio.org')) return 'COMA Ohio news';
  if (url.includes('/clubs/')) return 'Club page';
  if (url.includes('/public/tournament/')) return 'Live scoreboard';
  return fallback;
}

const TuskersLiveStatusBlogArticle = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPublishedPost(LIVE_STATUS_SLUG);
      setPost(data);
    } catch (err) {
      setError(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  useBlogPostMeta(post);

  if (loading) {
    return (
      <div className="tuskers-live">
        <p className="tuskers-live__status">Loading live status…</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="tuskers-live">
        <Link to="/blog" className="tuskers-live__back">← Back to blog</Link>
        <p className="tuskers-live__status tuskers-live__status--error">{error || 'Post not found'}</p>
      </div>
    );
  }

  const parsed = parseTuskersStatusBody(post.body);
  const maveliSection = parsed.sections.find((section) => section.kind === 'maveli');
  const standingsSection = parsed.sections.find((section) => section.kind === 'standings');
  const tuskersMatches = parsed.sections.find((section) => section.title.startsWith('Columbus Tuskers —'));
  const highlightsSection = parsed.sections.find((section) => section.kind === 'highlights');
  const tournamentSection = parsed.sections.find((section) => section.kind === 'tournaments');
  const resultsSection = parsed.sections.find((section) => section.title === 'Recent results');
  const linksSection = parsed.sections.find((section) => section.kind === 'links');

  const liveFixtures = tournamentSection?.tournaments?.flatMap((tournament) => tournament.fixtures || [])
    .filter((fixture) => fixture.live) || [];

  return (
    <article className="tuskers-live">
      <header className="tuskers-live__hero">
        <div className="tuskers-live__hero-pattern" aria-hidden="true" />
        <div className="tuskers-live__hero-content">
          <Link to="/blog" className="tuskers-live__back">← Back to blog</Link>
          <div className="tuskers-live__hero-top">
            {post.clubLogoUrl && (
              <img
                src={mediaUrl(post.clubLogoUrl)}
                alt=""
                className="tuskers-live__logo"
              />
            )}
            <div>
              <p className="tuskers-live__kicker">
                <span className="tuskers-live__badge">Live status</span>
                <span className="tuskers-live__badge tuskers-live__badge--cup">Maveli Cup 2026</span>
                {parsed.lastUpdated && <span>Updated {parsed.lastUpdated}</span>}
              </p>
              <h1>{post.title}</h1>
              {post.excerpt && <p className="tuskers-live__excerpt">{post.excerpt}</p>}
            </div>
          </div>
          <div className="tuskers-live__hero-stats">
            <div className="tuskers-live__stat">
              <span className="tuskers-live__stat-value">{standingsSection?.standings?.length || 0}</span>
              <span className="tuskers-live__stat-label">Group C teams</span>
            </div>
            <div className="tuskers-live__stat tuskers-live__stat--gold">
              <span className="tuskers-live__stat-value">
                {standingsSection?.standings?.find((row) => /tusker/i.test(row.club))?.points ?? '—'}
              </span>
              <span className="tuskers-live__stat-label">Tuskers points</span>
            </div>
            <div className="tuskers-live__stat tuskers-live__stat--live">
              <span className="tuskers-live__stat-value">{liveFixtures.length}</span>
              <span className="tuskers-live__stat-label">Live now</span>
            </div>
            <div className="tuskers-live__stat">
              <span className="tuskers-live__stat-value">{tuskersMatches?.items?.length || 0}</span>
              <span className="tuskers-live__stat-label">Tuskers fixtures</span>
            </div>
          </div>
        </div>
      </header>

      <div className="tuskers-live__shell">
        {maveliSection && (
          <section className="tuskers-live__panel tuskers-live__panel--accent">
            <div className="tuskers-live__panel-head">
              <h2>{maveliSection.title}</h2>
              <p>Official NAMSL Baltimore tournament feed</p>
            </div>
            <div className="tuskers-live__chip-row">
              {maveliSection.links.map((link) => (
                <a
                  key={link.url}
                  href={resolveHref(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tuskers-live__chip"
                >
                  {linkLabel(link.url, link.label)}
                </a>
              ))}
            </div>
          </section>
        )}

        {standingsSection?.standings?.length > 0 && (
          <section className="tuskers-live__panel">
            <h2>{standingsSection.title}</h2>
            <div className="tuskers-live__standings">
              <div className="tuskers-live__standings-head">
                <span>#</span>
                <span>Club</span>
                <span>Pts</span>
                <span>Record</span>
              </div>
              {standingsSection.standings.map((row) => (
                <div
                  key={`${row.position}-${row.club}`}
                  className={`tuskers-live__standings-row${/tusker/i.test(row.club) ? ' tuskers-live__standings-row--team' : ''}`}
                >
                  <span className="tuskers-live__standings-pos">{row.position}</span>
                  <span className="tuskers-live__standings-club">{row.club}</span>
                  <span className="tuskers-live__standings-pts">{row.points}</span>
                  <span className="tuskers-live__standings-record">
                    {row.wins != null
                      ? `${row.wins}W ${row.draws}D ${row.losses}L · ${row.goalsFor}–${row.goalsAgainst}`
                      : row.rawRecord}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {tuskersMatches?.items?.length > 0 && (
          <section className="tuskers-live__panel">
            <h2>{tuskersMatches.title}</h2>
            <div className="tuskers-live__match-grid">
              {tuskersMatches.items.map((item, index) => (
                <article key={`${item.text}-${index}`} className="tuskers-live__match-card">
                  <div className="tuskers-live__match-top">
                    {item.live && <span className="tuskers-live__live-pill">Live</span>}
                    {item.scheduled && !item.live && (
                      <span className="tuskers-live__scheduled-pill">Scheduled</span>
                    )}
                    {item.score && <span className="tuskers-live__score-pill">{item.score}</span>}
                  </div>
                  <p className="tuskers-live__match-text">{item.text}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {highlightsSection && (
          <section className="tuskers-live__panel tuskers-live__panel--highlight">
            <h2>Community highlights</h2>
            {highlightsSection.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </section>
        )}

        {tournamentSection?.tournaments?.length > 0 && (
          <section className="tuskers-live__panel">
            <h2>Tournament status</h2>
            <div className="tuskers-live__tournament-stack">
              {tournamentSection.tournaments.map((tournament, index) => (
                <article key={`${tournament.title}-${index}`} className="tuskers-live__tournament-card">
                  <h3>{tournament.title}</h3>
                  {tournament.fixtures.map((fixture, fixtureIndex) => (
                    <div key={`${fixture.text}-${fixtureIndex}`} className="tuskers-live__fixture">
                      <div className="tuskers-live__fixture-main">
                        {fixture.live && <span className="tuskers-live__live-pill">Live</span>}
                        <span>{fixture.text}</span>
                      </div>
                      {fixture.goals?.map((goal, goalIndex) => (
                        <p key={goalIndex} className="tuskers-live__goal">⚽ {goal}</p>
                      ))}
                    </div>
                  ))}
                  {tournament.topScorers && (
                    <p className="tuskers-live__top-scorers">
                      <strong>Top scorers:</strong> {tournament.topScorers}
                    </p>
                  )}
                  {tournament.liveUrl && (
                    <Link to={resolveHref(tournament.liveUrl)} className="tuskers-live__cta">
                      Open live scoreboard →
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        {resultsSection && (
          <section className="tuskers-live__panel tuskers-live__panel--compact">
            <h2>Recent results</h2>
            {resultsSection.items.length > 0 ? (
              <ul className="tuskers-live__simple-list">
                {resultsSection.items.map((item, index) => (
                  <li key={index}>{item.text}</li>
                ))}
              </ul>
            ) : (
              <p>{resultsSection.paragraphs[0] || 'No recent results yet.'}</p>
            )}
          </section>
        )}

        {(linksSection?.links?.length > 0 || post.referenceLinks?.length > 0) && (
          <section className="tuskers-live__panel tuskers-live__panel--links">
            <h2>Follow the team</h2>
            <div className="tuskers-live__link-grid">
              {(post.referenceLinks || linksSection.links).map((link, index) => (
                <a
                  key={`${link.url}-${index}`}
                  href={resolveHref(link.url)}
                  target={link.url?.startsWith('http') ? '_blank' : undefined}
                  rel={link.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="tuskers-live__link-card"
                >
                  <span>{link.label || linkLabel(link.url, 'View link')}</span>
                  <span aria-hidden="true">→</span>
                </a>
              ))}
            </div>
          </section>
        )}

        <footer className="tuskers-live__footer">
          <p>
            {post.clubName && (
              <Link to={soccerPath(`/clubs/${post.clubId}`)} className="tuskers-live__club-link">
                {post.clubLogoUrl && (
                  <img src={mediaUrl(post.clubLogoUrl)} alt="" className="tuskers-live__club-logo" />
                )}
                {post.clubName}
              </Link>
            )}
            {post.publishedAt && <span>Published {formatBlogDate(post.publishedAt)}</span>}
            {post.authorName && <span>By {post.authorName}</span>}
          </p>
          <BlogShareBar post={post} />
        </footer>
      </div>
    </article>
  );
};

export default TuskersLiveStatusBlogArticle;
